import { HIDE_TOAST, showSuccessToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { i18nFilter } from "app/filters/i18nFilter";
import { getAccountsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/accountsFields";
import { getCompanyAsLeadsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/companyAsLeadsFields";
import { getContactsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/contactsFields";
import { getLeadsFields } from "app/pages/sales-intelligence/pages/manage-integration/configs/leadsFields";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import {
    addNewSettingsExport,
    deleteSettingExport,
    fetchDynamicsExportSettingsAsync,
    revertSettingsExport,
    saveExportSettingsAsync,
    saveDynamicsAllSettingsError,
    updateDynamicsSettingsExport,
} from "../../../store/actions/export/dynamics";
import { createExportSettingsDto, deleteSetting, enrichExportSettings, groupByRecordType } from "../../../utils/export";
import { convertIntegrationTypeForApiSave, updateSettings } from "../../../utils/utils";
import { managementActions } from "../../action-creators";
import { getPreparedExportSettings } from "../../action-utils";
import { selectCacheToken, selectCurrentExportTab } from "../../selectors/common";
import {
    makeSelectDynamicsColumns,
    selectDynamicsCurrentSettings,
    selectDynamicsPrevSettingsByCurrentType,
    selectDynamicsSettings,
} from "../../selectors/dynamics/exportSelectors";
import type { ExportName, ExportSetting, NewExportSetting } from "../../type";
import type { DynamicsPreDefinedType } from "../../types/shared";

export const fetchDynamicsExportSettingsThunk =
    (signal: AbortSignal) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(fetchDynamicsExportSettingsAsync.request());
        try {
            const settings = await deps.si.api.dynamicsExportSettings.fetchDynamicsExportSettings(signal);
            const { accounts, contacts, leads, companyAsLeads } = groupByRecordType(settings);

            dispatch(
                fetchDynamicsExportSettingsAsync.success({
                    accounts: enrichExportSettings(accounts, getAccountsFields()),
                    contacts: enrichExportSettings(contacts, getContactsFields("dynamics")),
                    leads: enrichExportSettings(leads, getLeadsFields()),
                    companyAsLeads: enrichExportSettings(companyAsLeads, getCompanyAsLeadsFields()),
                }),
            );
        } catch (error) {
            dispatch(fetchDynamicsExportSettingsAsync.failure(error));
        }
    };

export const updateDynamicsExportSettingsThunk =
    (oldSetting: ExportSetting, newSetting: NewExportSetting, exportName: ExportName, isCrmEnrichmentEnabled = false) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const typeName = selectCurrentExportTab(getState());
        const settings: ExportSetting[] = selectDynamicsCurrentSettings(getState());
        const columns = makeSelectDynamicsColumns(typeName)(getState());

        const updatedSettings = updateSettings({
            settings,
            oldSetting,
            exportName,
            newSetting,
            columns,
            isCrmEnrichmentEnabled,
        });
        dispatch(updateDynamicsSettingsExport({ typeName, settings: updatedSettings }));
    };

const saveDynamicsExportSettingsThunk =
    (typeName: ExportName) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(saveExportSettingsAsync.request());

        try {
            const state = getState();
            const settings: ExportSetting[] = selectDynamicsSettings(state)[typeName];

            const mappedSettings = createExportSettingsDto(settings);

            const exportName = convertIntegrationTypeForApiSave(typeName) as DynamicsPreDefinedType;

            const { value } = await deps.si.api.dynamicsExportSettings.saveDynamicsExportSettings(
                { fields: mappedSettings },
                exportName,
            );

            if (value) {
                dispatch(saveExportSettingsAsync.success({ typeName, settings }));
            } else {
                dispatch(saveExportSettingsAsync.failure("Couldn't export to Dynamics"));
            }
            return value;
        } catch (error) {
            dispatch(saveExportSettingsAsync.failure(error));
            return false;
        }
    };

export const addSettingsDynamicsExportThunk = (settings: string[], exportName: ExportName) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const typeName = selectCurrentExportTab(getState());
        const existingSettings = selectDynamicsCurrentSettings(getState());
        const preparedSettings = getPreparedExportSettings({
            settings,
            exportName,
            integration: "dynamics",
            existingSettings,
        });

        dispatch(addNewSettingsExport({ typeName, settings: preparedSettings }));
    };
};

export const deleteDynamicsSettingLocalThunk = (field: string) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const typeName = selectCurrentExportTab(getState());
        const settings: ExportSetting[] = selectDynamicsCurrentSettings(getState());
        const preparedSettings = settings.filter((setting) => setting.field !== field);

        dispatch(updateDynamicsSettingsExport({ typeName, settings: preparedSettings }));
    };
};

export const deleteDynamicsExportSettingThunk = (settingToDelete: ExportSetting) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const settings: ExportSetting[] = selectDynamicsCurrentSettings(getState());
        const typeName = selectCurrentExportTab(getState());

        const { updatedSettings, deletedSetting } = deleteSetting(settings, settingToDelete);
        if (deletedSetting) {
            dispatch(deleteSettingExport({ typeName, settings: updatedSettings }));

            const timestamp = Date.now();
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text: i18nFilter()("si.management.sf.settings_export.toast.delete.content", {
                            fieldName: deletedSetting.title,
                        }),
                        onClick: () =>
                            dispatch({
                                type: HIDE_TOAST,
                                toast: { timestamp },
                            }),
                        linkText: i18nFilter()("si.management.sf.settings_export.toast.delete.btn.label"),
                    }),
                    timestamp,
                ),
            );
        }
    };
};

export const revertDynamicsSettingsThunk = () => {
    return (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const settings = selectDynamicsPrevSettingsByCurrentType(getState());
        const typeName = selectCurrentExportTab(getState());

        dispatch(revertSettingsExport({ typeName, settings }));
    };
};

export const getDynamicsColumnsThunk =
    (signal: AbortSignal) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(managementActions.dynamics.getDynamicsColumnsAsync.request());

        const cacheToken = selectCacheToken(getState());
        try {
            const [accounts, contacts, leads] = await Promise.all([
                deps.si.api.management.getDynamicsColumns("account", cacheToken, signal),
                deps.si.api.management.getDynamicsColumns("contact", cacheToken, signal),
                deps.si.api.management.getDynamicsColumns("lead", cacheToken, signal),
            ]);

            dispatch(
                managementActions.dynamics.getDynamicsColumnsAsync.success({
                    accounts,
                    contacts,
                    leads,
                    companyAsLeads: leads,
                }),
            );
        } catch (error) {
            dispatch(managementActions.dynamics.getDynamicsColumnsAsync.failure(error));
        }
    };

export const saveDynamicsAllSettingsThunk =
    (changedTabs: ExportName[]) =>
    async (dispatch: ThunkDispatchCommon): Promise<{ successfullySavedSettings: ExportName[] } | undefined> => {
        try {
            const saveSettingPromises = changedTabs.map((tabName) => {
                return dispatch(saveDynamicsExportSettingsThunk(tabName)).then(
                    (isSuccess) => [tabName, isSuccess] as [ExportName, boolean],
                );
            });
            const saveSettingResults = await Promise.all(saveSettingPromises);

            const successfullySavedSettings = saveSettingResults
                .filter(([_, isSuccess]) => isSuccess)
                .map(([exportName]) => exportName);

            return { successfullySavedSettings };
        } catch (error) {
            dispatch(saveDynamicsAllSettingsError(error));
        }
    };
